import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Category, ProjectDetail, ProjectSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBlock } from '@/components/site/cta-block'
import { HeroHeading } from '@/components/site/hero-heading'
import { WorkListing } from '@/components/landing/work/work-listing'
import { WorkFocus } from '@/components/landing/work/work-focus'
import { WorkApproach } from '@/components/landing/work/work-approach'

import {
    getCategories,
    getProjectBySlug,
    getProjects
} from '@/lib/content'

interface WorkPageProps {
    projects: ProjectSummary[]
    categories: Category[]
    featured: ProjectDetail | null
}

const WorkPage: FC<WorkPageProps> = ({ projects, categories, featured }): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Work"
                description="Web platforms, websites, WebGIS and mobile apps built by Zaki Hatta, a software engineer from Padang, Indonesia."
            />

            <section
                id='hero'
                className='relative z-[2] flex h-[24vh] w-full items-end justify-center overflow-clip bg-background px-4 pb-6 pt-hero md:h-[39vh] md:pt-section-md lg:h-[50vh] lg:px-6 lg:pt-hero'
            >
                <div className='flex flex-1 flex-col items-center'>
                    <HeroHeading first="Work" connector="that" second="Ships" />
                </div>
            </section>

            <WorkListing projects={projects} categories={categories} />

            {featured && <WorkFocus project={featured} />}

            <WorkApproach />
            <CtaBlock />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WorkPageProps> = async () => {
    try {
        const [projects, categories] = await Promise.all([getProjects(), getCategories()])
        const featuredSlug = (projects.find((project) => project.featured) ?? projects[0])?.slug
        const featured = featuredSlug ? await getProjectBySlug(featuredSlug) : null

        return { props: { projects, categories, featured }, revalidate: 60 }
    }
    catch (error) {
        console.error('Work page content could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { projects: [], categories: [], featured: null }, revalidate: 10 }
    }
}

export default WorkPage
